import { useDebouncedValue } from "@tanstack/react-pacer/debouncer";
import {
  type DefaultError,
  type QueryKey,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  type WithRequired,
  hashKey,
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import * as React from "react";

function useDebounce<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  createQuery: (
    searchTerm: string,
  ) => WithRequired<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    "queryKey"
  >,
  searchTerm: string,
  debouncerKey?: string,
): UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;
function useDebounce<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
>(
  createQuery: (
    searchTerm: string,
  ) => WithRequired<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
    "queryKey"
  >,
  searchTerm: string,
  debouncerKey?: string,
): UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>;
function useDebounce<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = number,
>(
  createQuery: (
    searchTerm: string,
  ) => WithRequired<
    | UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
    | UseInfiniteQueryOptions<
        TQueryFnData,
        TError,
        TData,
        TQueryKey,
        TPageParam
      >,
    "queryKey"
  >,
  searchTerm: string,
  debouncerKey?: string,
) {
  const queryClient = useQueryClient();

  const freshQuery = React.useMemo(
    () => createQuery(searchTerm),
    [createQuery, searchTerm],
  );

  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, {
    key: debouncerKey,
    wait: () => {
      const queryData = queryClient.getQueryState(freshQuery.queryKey);

      // if we already have the query in our cache or the query is disabled, we don't need to debounce
      if (
        queryData ||
        freshQuery.enabled === false ||
        freshQuery.queryFn === skipToken
      ) {
        return 0;
      }

      return 200;
    },
  });

  return debouncedSearchTerm === searchTerm
    ? freshQuery
    : createQuery(debouncedSearchTerm);
}

export function useDebouncedQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  createQuery: (
    searchTerm: string,
  ) => WithRequired<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    "queryKey"
  >,
  searchTerm: string,
  debouncerKey?: string,
) {
  const query = useDebounce(createQuery, searchTerm, debouncerKey);

  const queryKeyHash = hashKey(query.queryKey);

  const result = useQuery({
    placeholderData:
      query.enabled === false || query.queryFn === skipToken
        ? undefined
        : keepPreviousData,
    staleTime: 10_000,
    ...query,
  });

  return { query: result, queryKeyHash };
}

export function useDebouncedInfiniteQuery<
  TQueryFnData,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  createQuery: (
    searchTerm: string,
  ) => WithRequired<
    UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>,
    "queryKey"
  >,
  searchTerm: string,
  debouncerKey?: string,
) {
  const query = useDebounce(createQuery, searchTerm, debouncerKey);

  const queryKeyHash = hashKey(query.queryKey);

  const result = useInfiniteQuery({
    placeholderData:
      query.enabled === false || query.queryFn === skipToken
        ? undefined
        : keepPreviousData,
    staleTime: 10_000,
    // if `debounced_search_term === search_term`, this has the same result. If
    // `debounced_search_term !== search_term`, we're fetching the cached data
    // until debounce is done.
    ...query,
  });

  return { query: result, queryKeyHash };
}
